import {
  MODULE_KEYS,
  MODULE_KEY_HELP,
  MODULE_KEY_LABELS,
  MODULE_ROLE_LABELS,
  MODULE_ROLES,
  type ModuleActivationDto,
  type ModuleKey,
  type ModuleRole,
  type UserDto,
} from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Power, ShieldCheck, Users } from 'lucide-react';
import { GlassPanel, PanelHeader } from '../../components/GlassPanel.js';
import { SkeletonPanel } from '../../components/Skeleton.js';
import { api } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';

interface AccessRow {
  userId: string;
  module: ModuleKey;
  role: ModuleRole;
}

/**
 * The module platform's admin surface (P0). Superadmin-only: switch modules on
 * or off for the installation, and grant each user a role per module. Every
 * change is written to the activity log by the API.
 */
export function ModulesAdmin() {
  const queryClient = useQueryClient();

  const activations = useQuery({
    queryKey: ['modules'],
    queryFn: () => api.get<ModuleActivationDto[]>('/modules'),
  });
  const users = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => api.get<UserDto[]>('/users?includeInactive=true'),
  });
  const access = useQuery({
    queryKey: ['modules', 'access'],
    queryFn: () => api.get<AccessRow[]>('/modules/access'),
  });

  const toggle = useMutation({
    mutationFn: ({ module, enabled }: { module: ModuleKey; enabled: boolean }) =>
      api.patch(`/modules/${module}`, { enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['modules'] });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });

  const setAccess = useMutation({
    mutationFn: ({
      userId,
      module,
      role,
    }: {
      userId: string;
      module: ModuleKey;
      role: ModuleRole | null;
    }) =>
      role === null
        ? api.delete(`/users/${userId}/modules/${module}`)
        : api.put(`/users/${userId}/modules/${module}`, { role }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['modules', 'access'] });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });

  if (activations.isLoading || users.isLoading || access.isLoading) {
    return <SkeletonPanel />;
  }

  const enabledOf = new Map((activations.data ?? []).map((a) => [a.module, a.enabled]));
  const roleOf = (userId: string, module: ModuleKey): ModuleRole | null =>
    (access.data ?? []).find((r) => r.userId === userId && r.module === module)?.role ?? null;

  const people = (users.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, 'nb'));

  return (
    <div className="space-y-6">
      {/* ── Aktivering ─────────────────────────────────────────────────── */}
      <GlassPanel>
        <PanelHeader
          title="Moduler"
          subtitle="Slå funksjonsområder på eller av for hele installasjonen. En avslått modul er usynlig — ingen meny, ingen data, ingen agenter."
          icon={<Power size={16} />}
        />
        <div className="grid gap-3 sm:grid-cols-3">
          {MODULE_KEYS.map((module) => {
            const enabled = enabledOf.get(module) ?? false;
            const busy = toggle.isPending && toggle.variables?.module === module;
            return (
              <div
                key={module}
                className={cn(
                  'border-hairline rounded-lg border p-4 transition',
                  enabled ? 'bg-accent/[0.06]' : 'bg-base/30',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-ink text-sm font-medium">{MODULE_KEY_LABELS[module]}</p>
                    <p className="text-ink-soft/80 mt-1 text-[11px] leading-snug">
                      {MODULE_KEY_HELP[module]}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    aria-label={`${enabled ? 'Slå av' : 'Slå på'} ${MODULE_KEY_LABELS[module]}`}
                    disabled={busy}
                    onClick={() => toggle.mutate({ module, enabled: !enabled })}
                    className={cn(
                      'relative h-5 w-9 shrink-0 rounded-full transition disabled:opacity-50',
                      enabled ? 'bg-accent' : 'bg-ink-soft/25',
                    )}
                  >
                    <span
                      className={cn(
                        'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all',
                        enabled ? 'left-[18px]' : 'left-0.5',
                      )}
                    />
                  </button>
                </div>
                <p
                  className={cn(
                    'mt-3 text-[11px] font-medium',
                    enabled ? 'text-accent' : 'text-ink-soft/70',
                  )}
                >
                  {enabled ? 'På' : 'Av'}
                </p>
              </div>
            );
          })}
        </div>
      </GlassPanel>

      {/* ── Tilgang per bruker ─────────────────────────────────────────── */}
      <GlassPanel padded={false}>
        <div className="px-5 pt-5">
          <PanelHeader
            title="Modultilgang per bruker"
            subtitle="Hvem som kan bruke hver modul, og i hvilken rolle. «Ingen» fjerner tilgangen. Superadmin har alltid tilgang til alt og vises ikke her."
            icon={<Users size={16} />}
          />
        </div>
        <div className="overflow-x-auto px-5 pb-5">
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr className="border-hairline border-b">
                <th className="py-2 pr-3 text-left font-medium">Bruker</th>
                {MODULE_KEYS.map((module) => (
                  <th key={module} className="px-2 py-2 text-left font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      {MODULE_KEY_LABELS[module]}
                      {!(enabledOf.get(module) ?? false) && (
                        <span className="text-ink-soft/60 text-[10px]">(av)</span>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {people.map((person) => (
                <tr key={person.id} className="border-hairline/60 border-b last:border-0">
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn('text-ink', !person.isActive && 'text-ink-soft line-through')}
                      >
                        {person.name}
                      </span>
                      {person.isSuperadmin && (
                        <span
                          title="Superadmin — har all tilgang"
                          className="text-accent inline-flex items-center"
                        >
                          <ShieldCheck size={13} aria-hidden />
                        </span>
                      )}
                    </div>
                    <p className="text-ink-soft/70 text-[11px]">{person.email}</p>
                  </td>
                  {MODULE_KEYS.map((module) => {
                    const current = roleOf(person.id, module);
                    const busy =
                      setAccess.isPending &&
                      setAccess.variables?.userId === person.id &&
                      setAccess.variables?.module === module;
                    return (
                      <td key={module} className="px-2 py-2">
                        <select
                          value={current ?? ''}
                          disabled={person.isSuperadmin || busy}
                          onChange={(e) =>
                            setAccess.mutate({
                              userId: person.id,
                              module,
                              role: e.target.value === '' ? null : (e.target.value as ModuleRole),
                            })
                          }
                          className="border-hairline bg-base/50 text-ink h-8 w-full rounded-lg border px-2 text-xs disabled:opacity-50"
                        >
                          <option value="">Ingen</option>
                          {MODULE_ROLES.map((role) => (
                            <option key={role} value={role}>
                              {MODULE_ROLE_LABELS[role]}
                            </option>
                          ))}
                        </select>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {setAccess.isError && (
          <p className="text-danger px-5 pb-4 text-xs">{(setAccess.error as Error).message}</p>
        )}
      </GlassPanel>
    </div>
  );
}
